"use client";
import React, { useState, useEffect } from "react";
import styles from "./BlogSearchSection.module.css";
import SearchIcon from "@mui/icons-material/Search";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import { useTranslation } from "react-i18next";

const categories = [
    { name: "E-Book", slug: "e-book" },
    { name: "Fact Sheets", slug: "fact-sheets" },
    { name: "New Blogs", slug: "new-blogs" },
    { name: "Retirees Equity Release", slug: "retirees-equity-release" },
    { name: "Insights", slug: "insights" },
];

// Static page search results
const pageSearchResults = [
    { title: "About", type: "Page", redirectTo: "/about" },
    { title: "Booking", type: "Page", redirectTo: "/booking" },
    { title: "Borrowing capacity calculator", type: "Page", redirectTo: "/borrowing-capacity-calculator" },
    { title: "Contact", type: "Page", redirectTo: "/contact" },
    { title: "Expat finance simplified for Australian property", type: "Page", redirectTo: "/expat-finance-simplified-for-australian-property" },
    { title: "FAQ", type: "Page", redirectTo: "/faq" },
    { title: "Finance for business growth", type: "Page", redirectTo: "/finance-for-business-growth" },
    { title: "Financing SMSF property investment", type: "Page", redirectTo: "/financing-smsf-property-investment" },
    { title: "Home loan comparison calculator", type: "Page", redirectTo: "/home-loan-comparison-calculator" },
    { title: "Home loan for your dream home", type: "Page", redirectTo: "/home-loan-for-your-dream-home" },
    { title: "Home loan health assessment tool", type: "Page", redirectTo: "/home-loan-health-assessment-tool" },
    { title: "Leading Coffs harbour finance brokers", type: "Page", redirectTo: "/leading-coffs-harbour-finance-brokers" },
    { title: "Loan calculators", type: "Page", redirectTo: "/loan-calculators" },
    { title: "Loan repayment calculator", type: "Page", redirectTo: "/loan-repayment-calculator" },
    { title: "Mortgage extra repayment calculator", type: "Page", redirectTo: "/mortgage-extra-repayment-calculator" },
    { title: "Mortgage offset account calculator", type: "Page", redirectTo: "/mortgage-offset-account-calculator" },
    { title: "Mortgage broker Malvern", type: "Page", redirectTo: "/mortgage-broker-malvern" },
    { title: "Personal budget planner", type: "Page", redirectTo: "/personal-budget-planner" },
    { title: "Property buying cost calculator", type: "Page", redirectTo: "/property-buying-cost-calculator" },
    { title: "Property selling cost calculator", type: "Page", redirectTo: "/property-selling-cost-calculator" },
    { title: "Rent vs buy calculator", type: "Page", redirectTo: "/rent-vs-buy-calculator" },
    { title: "Reverse mortgage calculator", type: "Page", redirectTo: "/reverse-mortgage-calculator" },
    { title: "Reverse mortgage", type: "Page", redirectTo: "/reverse-mortgage" },
    { title: "Solutions", type: "Page", redirectTo: "/solutions" },
    { title: "Stamp duty calculator", type: "Page", redirectTo: "/stamp-duty-calculator" },
    { title: "The power of refinancing", type: "Page", redirectTo: "/the-power-of-refinancing" },
    { title: "Unlocking doors through investment lending", type: "Page", redirectTo: "/unlocking-doors-through-investment-lending" },
    { title: "Videos", type: "Page", redirectTo: "/videos" },
    { title: "Want to refinance", type: "Page", redirectTo: "/want-to-refinance" },
    { title: "Webinar", type: "Page", redirectTo: "/webinars" }
];

const SearchPageRight = () => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState("");
    const router = useRouter();
    const searchParams = useSearchParams();

    // Sync searchQuery with URL parameter
    useEffect(() => {
        const query = searchParams.get('q');
        if (query) {
            setSearchQuery(query);
        } else {
            setSearchQuery("");
        }
    }, [searchParams]);

    const handleSearchChange = (e) => setSearchQuery(e.target.value);

    const handleSearch = () => {
        if (searchQuery.trim()) {
            router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    const handleCategoryClick = (slug) => {
        router.push(`/blogs/category/${slug}`);
    };

    return (
        <aside className={styles.searchPageRight}>
            <div className={styles.searchSearchBar}>
                <h3>{t('Search Site')}</h3>
                <div className={styles.searchInputWrapper}>
                    <input
                        type="text"
                        className={styles.searchInput}
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        placeholder={t('Type to search...')}
                        aria-label="Search site"
                        title="Search site"
                    />
                    <span
                        className={styles.searchIcon}
                        onClick={handleSearch}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        role="button"
                        tabIndex={0}
                        aria-label={t('Search')}
                    >
                        <SearchIcon style={{ fontSize: "3rem" }} />
                    </span>
                </div>
            </div>

            <div className={styles.searchSocials}>
                <h3>{t('Socials')}</h3>
                <div className={styles.icons}>
                    <div>
                        <Link href="https://facebook.com/KeyChoiceLending">
                            <FacebookIcon  sx={{height:"1.25em", width:"1.25em"}}/>
                        </Link>
                    </div>
                    <div >
                        <Link href="https://instagram.com/keychoicelending">
                            <InstagramIcon sx={{height:"1.25em", width:"1.25em"}}/>
                        </Link>
                    </div>
                    <div className={styles.LinkedInIcon}>
                        <Link href="https://www.linkedin.com/company/keychoicelending/">
                            <LinkedInIcon sx={{height:"1.25em", width:"1.25em"}}/>
                        </Link>
                    </div>
                </div>
            </div>

            <div className={styles.searchCategories}>
                <h3>{t('Categories')}</h3>
                <div className={styles.searchCategoriesItems}>
                    {categories.map(({ name, slug }) => (
                        <p
                            key={slug}
                            onClick={() => handleCategoryClick(slug)}
                            onKeyDown={(e) => e.key === "Enter" && handleCategoryClick(slug)}
                            role="button"
                            tabIndex={0}
                            aria-label={`View ${name} category`}
                        >
                      {t(name)}
                        </p>
                    ))}
                </div>
            </div>
        </aside>
    );
};

export default SearchPageRight;